import { Fragment, useMemo, useState } from "react"

// ** Third Party Components
import { Link, useLocation } from "react-router-dom"
import { ArrowRight2 } from "iconsax-reactjs"

// ** Store
import { useCommonStore } from "@pma/stores/common-store"

// ** Constants
import { userProfileBreadcrumbs } from "@pma/utils/constants"
import { USER_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

const HeaderBreadcrumbs = () => {
  const location = useLocation()
  const { translate } = useTranslator()
  const [isExpanded, setIsExpanded] = useState(false)

  const { headerBreadcrumbsList: storeBreadcrumbs, setHeaderBreadcrumbsListAction } =
    useCommonStore()

  // Show hardcoded breadcrumbs (Home > Users > User Profile) on the user profile route,
  // mirroring how the settings page handles its breadcrumbs.
  const isUserProfileRoute =
    location.pathname.startsWith(`${USER_PAGE_ROUTE}/`) &&
    location.pathname.replace(`${USER_PAGE_ROUTE}/`, "").length > 0

  const headerBreadcrumbsList = useMemo(
    () => (isUserProfileRoute ? userProfileBreadcrumbs : storeBreadcrumbs),
    [isUserProfileRoute, storeBreadcrumbs],
  )

  const handleSetBreadcrumbs = (segment: any) => {
    let isMatch = false
    const newBreadcrumbsList = headerBreadcrumbsList
      .map((breadcrumb) => {
        if (breadcrumb.path == segment.path) {
          isMatch = true
          return breadcrumb
        }

        return isMatch ? false : breadcrumb
      })
      .filter((item) => item != false)

    localStorage.setItem("breadcrumbs", JSON.stringify(newBreadcrumbsList))
    setHeaderBreadcrumbsListAction(newBreadcrumbsList)
  }

  const count = headerBreadcrumbsList.length
  const lastIndex = count - 1

  // Render a single breadcrumb item
  const renderBreadcrumbItem = (segment: any, isLast: boolean, index: number, isMobile: boolean = false) => {
    const textSizeClass = isMobile ? "text-xs" : "text-base"

    return isLast ? (
      <span
        key={index}
        className={`max-w-32 truncate font-[inter] ${textSizeClass} font-semibold text-neutral-800 2xl:max-w-max`}
        title={translate(segment.title)}
      >
        {translate(segment.title)}
      </span>
    ) : (
      <Link
        key={index}
        to={segment.path}
        className={`font-[inter] ${textSizeClass} font-normal text-[#BEBEC8] hover:text-neutral-600 transition-colors`}
        onClick={() => handleSetBreadcrumbs(segment)}
      >
        {translate(segment.title)}
      </Link>
    )
  }

  // Collapsed view: shows "..." > last item, expands on click
  const renderCollapsedBreadcrumbs = (isMobile: boolean = false) => {
    const arrowSize = isMobile ? "12" : "14"
    const textSizeClass = isMobile ? "text-xs" : "text-base"

    // If only 1 item, just show it
    if (count <= 1) {
      return headerBreadcrumbsList.map((segment, index) => (
        <Fragment key={index}>
          {renderBreadcrumbItem(segment, index === lastIndex, index, isMobile)}
        </Fragment>
      ))
    }

    const last = headerBreadcrumbsList[lastIndex]
    const previousItems = headerBreadcrumbsList.slice(0, lastIndex)

    return (
      <>
        {/* Collapsed "..." button or expanded previous items */}
        {isExpanded ? (
          // Show all previous items when expanded
          previousItems.map((segment, index) => (
            <Fragment key={index}>
              {renderBreadcrumbItem(segment, false, index, isMobile)}
              <ArrowRight2 size={arrowSize} className="text-[#BEBEC8] flex-shrink-0" />
            </Fragment>
          ))
        ) : (
          // Show "..." when collapsed
          <>
            <button
              onClick={() => setIsExpanded(true)}
              className={`font-[inter] ${textSizeClass} font-normal text-[#BEBEC8] hover:text-neutral-600 transition-colors cursor-pointer select-none px-1`}
              title="Show full path"
            >
              ...
            </button>
            <ArrowRight2 size={arrowSize} className="text-[#BEBEC8] flex-shrink-0" />
          </>
        )}

        {/* Always show the last (active) item */}
        {renderBreadcrumbItem(last, true, lastIndex, isMobile)}
      </>
    )
  }

  return (
    <div className="flex items-center gap-1">
      {/* Desktop */}
      <div className="hidden items-center gap-1 xl:flex">
        {renderCollapsedBreadcrumbs(false)}
      </div>

      {/* Mobile */}
      <div className="flex items-center gap-1 xl:hidden">
        {renderCollapsedBreadcrumbs(true)}
      </div>

      {/* Collapse button when expanded */}
      {isExpanded && count > 1 && (
        <button
          onClick={() => setIsExpanded(false)}
          className="ml-1 text-xs text-[#BEBEC8] hover:text-neutral-600 transition-colors"
          title="Collapse"
        >
          ←
        </button>
      )}
    </div>
  )
}

export default HeaderBreadcrumbs